import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Divider, List, Modal, Tabs, message } from 'antd';
import http, { resolveApiUrl } from '../api/http';

const resolveCheckinPhotoUrl = (rawUrl) => {
  const input = String(rawUrl || '').trim();
  if (!input) return '';

  const absolute = resolveApiUrl(input);
  const token = String(localStorage.getItem('token') || '').trim();
  if (!token) return absolute;

  try {
    const url = new URL(absolute, window.location.origin);
    if (!url.searchParams.get('token') && !url.searchParams.get('access_token')) {
      url.searchParams.set('token', token);
    }
    return url.toString();
  } catch (e) {
    const joiner = absolute.includes('?') ? '&' : '?';
    return `${absolute}${joiner}token=${encodeURIComponent(token)}`;
  }
};

const dataUrlToBlob = (dataUrl) => {
  const [meta, base64] = String(dataUrl).split(',');
  const m = /data:([^;]+);base64/.exec(meta || '');
  const mime = m?.[1] || 'image/jpeg';
  const bin = atob(base64 || '');
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

const useIntersection = (onIntersect) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onIntersect();
      },
      { root: null, rootMargin: '200px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [onIntersect]);
  return ref;
};

const CheckinModal = ({ open, onClose, onSuccess }) => {
  const [cameraError, setCameraError] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [capturedDataUrl, setCapturedDataUrl] = useState('');

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      try {
        s.getTracks().forEach((t) => t.stop());
      } catch (e) {
        // ignore
      }
    }
    streamRef.current = null;
    setStreaming(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError('');
    setCapturedDataUrl('');
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        setCameraError('当前浏览器不支持摄像头访问');
        return;
      }
      stopCamera();
      const constraints = {
        audio: false,
        video: {
          facingMode: { ideal: 'user' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        try {
          await videoRef.current.play();
        } catch (e) {
          // ignore
        }
      }
      setStreaming(true);
    } catch (e) {
      setCameraError(e?.message || '无法打开摄像头');
      stopCamera();
    }
  }, [stopCamera]);

  useEffect(() => {
    if (open) {
      startCamera();
      return undefined;
    }
    stopCamera();
    setCapturedDataUrl('');
    setCameraError('');
    return undefined;
  }, [open, startCamera, stopCamera]);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const onCapture = useCallback(() => {
    setCameraError('');
    const video = videoRef.current;
    if (!video) return;
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, width, height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedDataUrl(dataUrl);
    stopCamera();
  }, [stopCamera]);

  const onRetake = useCallback(() => {
    setCapturedDataUrl('');
    startCamera();
  }, [startCamera]);

  const onSubmit = useCallback(async () => {
    if (!capturedDataUrl) {
      message.warning('请先自拍');
      return;
    }
    setUploading(true);
    try {
      const blob = dataUrlToBlob(capturedDataUrl);
      const form = new FormData();
      form.append('file', blob, `daily-checkin-${Date.now()}.jpg`);
      await http.post('/behavior/daily-checkin/checkins', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('打卡成功');
      onSuccess?.();
      onClose?.();
    } catch (e) {
      message.error(e?.response?.data?.error || e?.message || '打卡失败');
    } finally {
      setUploading(false);
    }
  }, [capturedDataUrl, onClose, onSuccess]);

  return (
    <Modal
      title="打卡"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {!!cameraError && <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={cameraError} />}

      {!capturedDataUrl ? (
        <>
          <div
            style={{
              width: '100%',
              background: 'var(--bg-secondary)',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
            }}
          >
            <video ref={videoRef} style={{ width: '100%', display: 'block' }} playsInline muted autoPlay />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <Button type="primary" onClick={startCamera} disabled={streaming}>
              打开前置摄像头
            </Button>
            <Button onClick={stopCamera} disabled={!streaming}>
              关闭
            </Button>
            <Button type="primary" onClick={onCapture} disabled={!streaming}>
              拍照
            </Button>
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              width: '100%',
              background: 'var(--bg-secondary)',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
            }}
          >
            <img src={capturedDataUrl} alt="自拍预览" style={{ width: '100%', display: 'block' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <Button onClick={onRetake}>重拍</Button>
            <Button type="primary" onClick={onSubmit} loading={uploading}>
              提交打卡
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

const RecordsTab = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await http.get('/behavior/daily-checkin/my', {
        params: { page, pageSize },
      });
      const list = Array.isArray(data?.list) ? data.list : [];
      const total = Number(data?.total || 0);
      setItems((prev) => {
        const merged = [...prev, ...list];
        setHasMore(merged.length < total);
        return merged;
      });
      setPage((p) => p + 1);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, page, pageSize]);

  const reload = useCallback(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
  }, []);

  useEffect(() => {
    if (page === 1 && items.length === 0) load();
  }, [items.length, load, page]);

  const onModalSuccess = useCallback(() => {
    reload();
  }, [reload]);

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ color: 'var(--text-secondary)' }}>每人每天最多打卡 2 次</div>
        <Button type="primary" onClick={() => setModalOpen(true)}>
          去打卡
        </Button>
      </div>

      <Divider />

      {!!error && <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={error} />}
      <List
        dataSource={items}
        locale={{ emptyText: '暂无打卡记录' }}
        renderItem={(it) => (
          <List.Item key={it.id} style={{ alignItems: 'flex-start' }}>
            <div style={{ width: '100%' }}>
              <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                {it.created_at ? `打卡时间：${String(it.created_at).replace('T', ' ').slice(0, 19)}` : ''}
              </div>
              <div
                style={{
                  width: 240,
                  maxWidth: '100%',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
                  aspectRatio: '1 / 1',
                }}
              >
                <img
                  src={resolveCheckinPhotoUrl(it.photo_url)}
                  alt="打卡自拍"
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            </div>
          </List.Item>
        )}
      />

      <div style={{ padding: '12px 0', color: 'var(--text-secondary)' }}>
        {loading ? '加载中…' : hasMore ? (
          <Button type="link" onClick={load} disabled={loading} style={{ padding: 0 }}>
            加载更多
          </Button>
        ) : '已加载完'}
      </div>

      <CheckinModal open={modalOpen} onClose={() => setModalOpen(false)} onSuccess={onModalSuccess} />
    </div>
  );
};

const BoardTab = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await http.get('/behavior/daily-checkin/board', {
        params: { page, pageSize },
      });
      const list = Array.isArray(data?.list) ? data.list : [];
      const total = Number(data?.total || 0);
      setItems((prev) => {
        const merged = [...prev, ...list];
        setHasMore(merged.length < total);
        return merged;
      });
      setPage((p) => p + 1);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, page, pageSize]);

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (page === 1 && items.length === 0) load();
  }, [items.length, load, page]);

  const onIntersect = useCallback(() => {
    load();
  }, [load]);
  const sentinelRef = useIntersection(onIntersect);

  const gridItems = useMemo(() => items.filter(Boolean), [items]);

  return (
    <div>
      {!!error && <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={error} />}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 8,
        }}
      >
        {gridItems.map((it) => (
          <div
            key={it.id}
            style={{
              width: '100%',
              borderRadius: 8,
              overflow: 'hidden',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
              aspectRatio: '1 / 1',
            }}
          >
            <img
              src={resolveCheckinPhotoUrl(it.photo_url)}
              alt="打卡自拍"
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        ))}
      </div>

      <div ref={sentinelRef} style={{ height: 1 }} />

      <div style={{ padding: '12px 0', color: 'var(--text-secondary)' }}>
        {loading ? '加载中…' : hasMore ? '继续下拉加载' : '已加载完'}
      </div>
    </div>
  );
};

const BehaviorDailyCheckinPage = () => {
  const items = useMemo(
    () => [
      { key: 'records', label: '打卡', children: <RecordsTab /> },
      { key: 'board', label: '打卡看板', children: <BoardTab /> },
    ],
    []
  );

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>日常打卡</h2>
      <Tabs items={items} />
    </div>
  );
};

export default BehaviorDailyCheckinPage;
